import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Slider from "react-slick";
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

import Background from '../../assets/img/website-images/Homepage-1.webp';
import Backgroundtwo from '../../assets/img/website-images/Homepage-2.webp';
import Backgroundthree from '../../assets/img/website-images/Homepage-3.webp';

/* ─── Arrow Components ─────────────────────────────────────────── */
function SampleNextArrow(props) {
    const { className, style, onClick } = props;
    return (
        <button type="button" className={`slick-next ${className}`} style={{ ...style }} onClick={onClick}>
            <i className="far fa-angle-right"></i>
        </button>
    );
}

function SamplePrevArrow(props) {
    const { className, style, onClick } = props;
    return (
        <button type="button" className={`slick-prev ${className}`} style={{ ...style }} onClick={onClick}>
            <i className="far fa-angle-left"></i>
        </button>
    );
}

/* ─── Slide Content Variants (Framer Motion) ───────────────────── */
const taglineVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
};

const headingVariants = {
    hidden: { opacity: 0, y: 50, skewY: 3 },
    visible: {
        opacity: 1,
        y: 0,
        skewY: 0,
        transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
    },
    exit: { opacity: 0, y: 30, transition: { duration: 0.4 } },
};

const paraVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.75, ease: 'easeOut', delay: 0.3 },
    },
    exit: { opacity: 0, x: -20, transition: { duration: 0.35 } },
};

const btnContainerVariants = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.18, delayChildren: 0.5 },
    },
};

const btnVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { duration: 0.55, ease: [0.34, 1.56, 0.64, 1] },
    },
};

/* ─── Slide Data ───────────────────────────────────────────────── */
const slides = [
    {
        bg: Background,
        tagline: 'Welcome to Anjanadri Institute of Pharmacy',
        heading: "Shaping Future Pharmacists with Knowledge and Care",
        para: "Begin your journey in pharmaceutical sciences at Anjanadri Institute of Pharmacy — where quality education, practical training, and professional values prepare students for successful careers in healthcare.",
    },
    {
        bg: Backgroundtwo,
        tagline: 'Welcome to Anjanadri Institute of Pharmacy',
        heading: 'Building Strong Foundations in Pharmaceutical Education',
        para: 'Affiliated to Rajiv Gandhi University of Health Sciences (RGUHS) and approved by the Government of Karnataka, the institute offers a comprehensive B.Pharm program designed to meet modern healthcare and industry standards.',
    },
    {
        bg: Backgroundthree,
        tagline: 'Welcome to Anjanadri Institute of Pharmacy',
        heading: 'Start Your Journey Toward a Successful Pharmacy Career',
        para: 'With modern laboratories, experienced faculty, and industry-oriented training, Anjanadri Institute of Pharmacy empowers students with the skills and confidence needed to excel in the pharmaceutical and healthcare sectors.',
    },
];

/* ─── Single Slide Content ─────────────────────────────────────── */
function SlideContent({ slide, isActive }) {
    return (
        <div
            className="single-slider slider-bg"
            style={{ backgroundImage: `url(${slide.bg})`, backgroundSize: 'cover' }}
        >
            <div className="container">
                <div className="row">
                    <div className="col-lg-7 col-md-7">
                        <div className="slider-content s-slider-content mt-130">
                            <AnimatePresence mode="wait">
                                {isActive && (
                                    <motion.div
                                        key={slide.heading}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                    >
                                        {/* Tagline */}
                                        <motion.h5 variants={taglineVariants}>
                                            {slide.tagline}
                                        </motion.h5>

                                        {/* Heading — word-by-word reveal */}
                                        <motion.h2
                                            variants={headingVariants}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            {slide.heading}
                                        </motion.h2>

                                        {/* Paragraph */}
                                        <motion.p variants={paraVariants}>
                                            {slide.para}
                                        </motion.p>

                                        {/* Buttons */}
                                        <motion.div
                                            className="slider-btn mt-30"
                                            variants={btnContainerVariants}
                                        >
                                            <motion.span variants={btnVariants} style={{ display: 'inline-block', marginRight: '15px' }}>
                                                <Link to="/about-us" className="btn ss-btn">
                                                    Discover More <i className="fal fa-long-arrow-right" />
                                                </Link>
                                            </motion.span>
                                            <motion.span variants={btnVariants} style={{ display: 'inline-block' }}>
                                                <Link to="/contact-us" className="btn ss-btn active">
                                                    Contact Us <i className="fal fa-long-arrow-right" />
                                                </Link>
                                            </motion.span>
                                        </motion.div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                    <div className="col-lg-5 col-md-5 p-relative"></div>
                </div>
            </div>
        </div>
    );
}

/* ─── Feature Card (GSAP on scroll via react-intersection-observer) */
function FeatureCard({ icon, title, description, link, isActive, index }) {
    const cardRef = useRef(null);
    const { ref: inViewRef, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

    // Merge refs
    const setRefs = (el) => {
        cardRef.current = el;
        inViewRef(el);
    };

    useEffect(() => {
        if (inView && cardRef.current) {
            gsap.fromTo(
                cardRef.current,
                { opacity: 0, y: 60, scale: 0.92 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.75,
                    ease: 'power3.out',
                    delay: index * 0.18,
                }
            );
        }
    }, [inView, index]);

    return (
        <div className="col-lg-4 col-md-12 col-sm-12">
            <div
                ref={setRefs}
                className={`services-box07${isActive ? ' active' : ''}`}
                style={{ opacity: 0 }} /* start hidden, GSAP reveals */
            >
                <div className="sr-contner">
                    <div className="icon">
                        {/* Icon bounce-in via Framer Motion */}
                        <motion.img
                            src={icon}
                            alt="icon"
                            whileHover={{ rotate: [0, -8, 8, -4, 0], scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                    <div className="text">
                        <h5>
                            <Link to="/about-us">{title}</Link>
                        </h5>
                        <p>{description}</p>
                        <Link to="/about-us">
                            Read More <i className="fal fa-long-arrow-right" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Sliderone() {
    const [activeSlide, setActiveSlide] = React.useState(0);

    const settings = {
        autoplay: true,
        autoplaySpeed: 10000,
        dots: false,
        fade: true,
        arrows: true,
        prevArrow: <SamplePrevArrow />,
        nextArrow: <SampleNextArrow />,
        beforeChange: (_, next) => setActiveSlide(next),
        responsive: [
            { breakpoint: 1200, settings: { dots: false, arrows: false } },
        ],
    };

    const cards = [
        {
            icon: '/assets/img/icon/sve-icon4.png',
            title: 'Industry-Ready Curriculum',
            description:
                'A practical, RGUHS-aligned B.Pharm program that blends laboratory training, research exposure, and real-world pharmaceutical industry insights.',
            isActive: false,
        },
        {
            icon: '/assets/img/icon/sve-icon5.png',
            title: 'State-of-the-Art Laboratories',
            description:
                'Fully equipped labs for Pharmaceutics, Pharmacology, Pharmaceutical Chemistry, and Pharmacognosy — designed for hands-on discovery and research.',
            isActive: true,
        },
        {
            icon: '/assets/img/icon/sve-icon6.png',
            title: 'Career & Placement Support',
            description:
                'Dedicated mentorship, industry partnerships, and placement assistance to help every graduate step confidently into their chosen career path.',
            isActive: false,
        },
    ];

    return (
        <>
            {/* ── Slider Section ── */}
            <section id="home" className="slider-area fix p-relative">
                <Slider
                    className="slider-active"
                    style={{ background: '#141b22' }}
                    {...settings}
                >
                    {slides.map((slide, i) => (
                        <div key={i}>
                            <SlideContent slide={slide} isActive={activeSlide === i} />
                        </div>
                    ))}
                </Slider>
            </section>

            {/* ── Feature Cards Section ── */}
            <section className="service-details-two p-relative">
                <div className="container">
                    <div className="row">
                        {cards.map((card, i) => (
                            <FeatureCard key={i} index={i} {...card} />
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}

export default Sliderone;
